/**
 * Shared guess-vs-solution matching.
 *
 * GameContext uses these to grade a guess as it is made. The finished-game
 * "Most Common Guesses" list uses the same rules to mark which community
 * guesses were right, so it never needs a correctness flag from the server
 * (the get_top_guesses RPC is readable with the public key, so its
 * is_correct field must not be trusted or needed for display).
 */

export const sanitizeGuess = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/\$+/g, '')
    .replace(/\(.*?\)/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const extractGuessSymbol = (value: string): string | null => {
  const normalized = value.trim();

  const labeledMatch = normalized.match(/^\$([A-Za-z0-9.-]+):/);
  if (labeledMatch) {
    return labeledMatch[1].toUpperCase();
  }

  const parenMatch = normalized.match(/\(([A-Za-z0-9.-]+)\)/);
  if (parenMatch) {
    return parenMatch[1].toUpperCase();
  }

  const tickerOnlyMatch = normalized.match(/^\$?([A-Za-z0-9.-]+)$/);
  if (tickerOnlyMatch) {
    return tickerOnlyMatch[1].toUpperCase();
  }

  return null;
};

export interface GuessSolution {
  Symbol: string;
  Security?: string | null;
}

/**
 * True when a guess (as typed or as stored, e.g. "$AAPL: Apple Inc.") names
 * the solution: same ticker (trimmed, case-insensitive), or same company name.
 */
export const isGuessForSolution = (
  guessText: string | null | undefined,
  solution: GuessSolution | null | undefined
): boolean => {
  if (!guessText || !solution?.Symbol) return false;

  const solutionSymbol = solution.Symbol.trim().toUpperCase();
  if (!solutionSymbol) return false;

  const guessedSymbol = extractGuessSymbol(guessText);
  if (guessedSymbol && guessedSymbol === solutionSymbol) return true;

  const solutionName = solution.Security ? sanitizeGuess(solution.Security) : '';
  return solutionName !== '' && sanitizeGuess(guessText) === solutionName;
};
